import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  HeartPulse,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Sparkles,
  Syringe,
  TrendingUp,
  UserRound,
  Droplets,
  Activity,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/sebaceous-cyst-removal-mumbai";
const LIPOMA_PATH = "/lipoma-removal-surgery-mumbai";

const CYST_FAQS = [
  {
    question: "Will a sebaceous cyst go away without treatment?",
    answer:
      "No. A sebaceous cyst does not resolve spontaneously. The cyst wall, once formed, continues to produce keratin material. The cyst may fluctuate in apparent size if it partially discharges through the punctum, creating the impression of reduction, but the wall remains and the cyst will refill. Permanent removal requires surgical excision of the entire cyst sac.",
  },
  {
    question: "Why does a cyst come back after drainage?",
    answer:
      "Drainage removes the keratin contents of the cyst but does not remove the cyst wall. The lining cells of the wall continue producing keratin after drainage and the cyst refills within weeks to months. This is why drainage is used only to treat acute infection and pain relief, not as a definitive cure. Complete excision of the cyst wall is the only treatment that prevents recurrence.",
  },
  {
    question: "Is it safe to have a cyst removed when it is infected?",
    answer:
      "Operating on an actively infected cyst is not recommended. The infection disrupts the tissue planes between the cyst wall and surrounding tissue, making complete removal more difficult and increasing the risk of wound healing complications. The standard approach is to drain the infection for immediate relief, complete a course of antibiotics, wait for the inflammation to fully settle over 4 to 6 weeks, and then perform complete elective excision.",
  },
  {
    question: "How visible will the scar be after cyst removal?",
    answer:
      "This depends on the location and the size of the cyst. Dr. Jain places incisions along natural skin lines and uses fine suture closure to minimize scar width. On cosmetically sensitive areas including the face and neck, the minimal excision technique reduces incision length further. Most sebaceous cyst scars are narrow lines that fade to near-invisible within 6 to 12 months with appropriate scar care. On areas with favorable skin texture such as the back, scars also heal well though they are less critical cosmetically.",
  },
  {
    question: "Can I squeeze the cyst myself?",
    answer:
      "Squeezing a cyst provides temporary pressure relief by forcing some keratin out through the punctum. It does not remove the cyst wall and the cyst refills. More importantly, squeezing can rupture the cyst wall, releasing keratin material into the surrounding tissue. This causes intense local inflammation that is painful and difficult to distinguish from infection, and leaves residual scar tissue that makes subsequent surgical excision more technically demanding. Squeezing is not recommended.",
  },
  {
    question: "Do I need a biopsy after cyst removal?",
    answer:
      "The excised specimen is routinely sent for histological analysis after cyst removal. This confirms the benign diagnosis and excludes any unexpected finding in the removed tissue. Most results confirm epidermoid cyst. The histological report is reviewed at the post-operative visit.",
  },
  {
    question: "How long do I need off work after sebaceous cyst removal?",
    answer:
      "For most locations, patients return to desk work the same day or the following day. The procedure is performed under local anesthesia with no sedation required. Physical work or work involving the treated area may require 1 to 2 days off depending on the location of the cyst. No extended absence from work is needed for standard sebaceous cyst excision.",
  },
  {
    question: "What is the difference between a sebaceous cyst and an abscess?",
    answer:
      "A sebaceous cyst is a benign epithelial-lined sac filled with keratin. An abscess is a collection of pus caused by bacterial infection. An infected sebaceous cyst can look similar to an abscess because both present as a red, tender, fluctuant swelling. The distinction matters because treatment differs: an infected cyst requires drainage followed by elective cyst excision, while a primary skin abscess requires drainage alone without the subsequent need to remove an underlying cyst. Ultrasound distinguishes between the two when clinical examination is inconclusive.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Sebaceous cyst",
      alternateName: ["Epidermoid cyst", "Epidermal inclusion cyst"],
      description:
        "Benign skin cyst lined by epithelium and filled with keratin, often with a central punctum; may become infected; definitively treated by complete surgical excision of the cyst wall.",
      possibleTreatment: ["Complete surgical excision", "Minimal excision technique"],
      signOrSymptom: [
        { "@type": "MedicalSignOrSymptom", name: "Skin lump" },
        { "@type": "MedicalSignOrSymptom", name: "Central punctum" },
        { "@type": "MedicalSignOrSymptom", name: "Keratin discharge" },
        { "@type": "MedicalSignOrSymptom", name: "Localized infection" },
      ],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Sebaceous cyst excision",
      description:
        "Local anesthesia removal of an epidermoid cyst with intact cyst wall, layered closure, and routine histology.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Skin" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: CYST_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function CystJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Sebaceous Cyst Removal", href: "/sebaceous-cyst-removal-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Skin &amp; Soft Tissue</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Sebaceous Cyst Removal
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Complete Epidermoid Cyst Excision by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Cyst Removal" icon="🔬" size="hero"  imageSrc={HERO.sebaceousCystRemoval} imageAlt="Cyst removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What is a Sebaceous Cyst?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A sebaceous cyst, more accurately called an epidermoid cyst, is a benign, slow-growing skin lump that develops from a blocked hair follicle or skin gland. The cyst is a sac lined with skin cells that fills with keratin, a soft, pale material that has a characteristic odor when the cyst is opened or ruptures. The cyst is not cancer. It does not spread and in the vast majority of cases causes no harm beyond its physical presence and the complications that develop when it becomes infected.
            </p>
            <p>
              The most recognizable feature of a sebaceous cyst is the central punctum, a small darkened opening on the skin surface directly over the cyst. This is where the blocked follicle or gland originally obstructed. Pressing the cyst may cause discharge through the punctum, which provides temporary relief of pressure but does not remove the cyst wall and does not prevent the cyst from refilling and recurring.
            </p>
            <p>
              Dr. Vicky Jain removes sebaceous cysts through complete surgical excision under local anesthesia. The procedure takes 20 to 30 minutes and requires no hospital admission. Complete removal of the entire cyst sac is the only treatment that prevents recurrence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Cyst Removal</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The key technical principle in sebaceous cyst removal is complete excision of the cyst sac. The cyst wall is a thin membrane that lines the cavity containing the keratin material. If any portion of this wall is left behind, the remaining lining cells continue to produce keratin and the cyst reforms at the same site. This is the reason cysts recur after drainage, after incomplete excision, and after rupture that leaves part of the wall in place. Dr. Jain excises the entire cyst including its wall in a single piece to ensure complete removal.
              </p>
              <p>
                Incision placement and size depend on the cyst&apos;s location and whether it is currently infected. An uninflamed cyst is easiest to remove completely because the tissue planes between the cyst wall and surrounding tissue are clean and well-defined. An infected or previously ruptured cyst has inflammation and scar tissue that obscures these planes, making complete removal more technically demanding. When a cyst is actively infected, Dr. Jain treats the infection first, then excises the cyst electively once the inflammation has resolved, rather than operating in an infected field where complete excision is more difficult and complication risk is higher.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhenRemovalSection() {
  const cards = [
    {
      title: "Increasing Size",
      icon: TrendingUp,
      copy: "A cyst that has been stable and begins to grow warrants removal. Larger cysts require larger incisions and produce more visible scars. Earlier removal when the cyst is smaller produces a better cosmetic outcome.",
    },
    {
      title: "Infection",
      icon: HeartPulse,
      copy: "An infected cyst becomes red, warm, tender, and painful. It may discharge spontaneously. Infected cysts are treated with antibiotics and drainage to resolve the infection, followed by elective excision once the inflammation settles. Attempting complete excision during active infection increases complication risk and recurrence.",
    },
    {
      title: "Recurrent Inflammation",
      icon: Activity,
      copy: "Some cysts repeatedly become inflamed without fully developing an infection. Recurrent inflammation indicates an unstable cyst that is likely to become more problematic over time. Elective removal during a settled period is recommended.",
    },
    {
      title: "Cosmetic Concern",
      icon: UserRound,
      copy: "A visible cyst on the face, scalp, neck, or other prominent area that affects appearance or causes self-consciousness is a valid indication for removal. Cosmetic concern does not require a physical symptom to justify treatment.",
    },
    {
      title: "Discharge",
      icon: Droplets,
      copy: "A cyst that regularly discharges through the punctum, releasing odorous material, is a practical nuisance and a hygiene concern. Complete removal eliminates the source of the discharge permanently.",
    },
    {
      title: "Diagnostic Uncertainty",
      icon: Syringe,
      copy: "When the clinical diagnosis of a sebaceous cyst is not certain, surgical excision with histological analysis of the specimen provides a definitive diagnosis. Any skin lump that does not have the typical features of a benign cyst should be excised and examined rather than observed.",
    },
  ];
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="when-removal-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">When Should a Sebaceous Cyst Be Removed?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_LIPOMA_ROWS = [
  {
    label: "Contents",
    cyst: "Keratin, a soft pale material with a characteristic odor.",
    lipoma: "Fat tissue.",
  },
  {
    label: "Texture",
    cyst: "Firm to tense.",
    lipoma: "Soft and doughy.",
  },
  {
    label: "Central Punctum",
    cyst: "Usually present. A small darkened opening visible on the skin surface.",
    lipoma: "Absent. No skin opening.",
  },
  {
    label: "Location in Tissue",
    cyst: "Sits within the skin layers.",
    lipoma: "Sits in the subcutaneous fat beneath the skin.",
  },
  {
    label: "Infection Risk",
    cyst: "Common. Can become infected and painful.",
    lipoma: "Rare. Does not typically become infected.",
  },
  {
    label: "Discharge",
    cyst: "May discharge keratin material through the punctum.",
    lipoma: "No discharge.",
  },
];

function CystVsLipomaSection() {
  const linkClass = "text-gold font-medium hover:text-deep-navy underline underline-offset-2 transition-colors";
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="cyst-vs-lipoma-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Sebaceous Cyst vs. Lipoma: How to Tell the Difference
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Sebaceous Cyst
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  <Link to={LIPOMA_PATH} className="hover:text-gold transition-colors">
                    Lipoma
                  </Link>
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_LIPOMA_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.cyst}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.lipoma}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Clinical examination distinguishes most sebaceous cysts from lipomas. In cases where the diagnosis is uncertain, ultrasound is the appropriate first investigation. Both are benign and both are treated by surgical excision, but the excision technique differs. The key technical difference is that sebaceous cyst removal must completely excise the cyst wall to prevent recurrence, while{" "}
          <Link to={LIPOMA_PATH} className={linkClass}>
            lipoma removal
          </Link>{" "}
          focuses on removing the fatty mass with its surrounding capsule.
        </p>
      </div>
    </section>
  );
}

function IfIgnoredSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="if-ignored-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Risks of Waiting</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">What Happens if a Sebaceous Cyst is Left Untreated?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Many sebaceous cysts remain stable for years without causing problems. The reason early treatment is preferable when removal is planned is practical rather than urgent: smaller cysts produce smaller scars, are easier to remove completely, and present less risk of the cyst rupturing before the planned excision. A cyst that ruptures spontaneously releases its keratin contents into the surrounding tissue, causing an intense inflammatory reaction that can mimic infection, is painful, and leaves residual scar tissue that makes subsequent complete excision more technically demanding.
            </p>
            <p>
              Cysts that become infected require a two-stage approach, drainage followed by elective excision, rather than the single-stage complete excision that is possible for an uninflamed cyst. Infected cysts are also more painful and create more disruption to daily activity than elective removal of an uninflamed cyst would. For patients who have decided they want the cyst removed, earlier removal during a settled phase consistently produces a better outcome than waiting until a complication forces treatment.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Complete Surgical Excision",
    body:
      "The gold standard treatment. The entire cyst, including the cyst wall, is removed through a small incision directly over the cyst. The wall is dissected free from the surrounding tissue and removed in one piece to ensure no lining cells remain. The incision is closed with fine sutures placed along natural skin lines. Results are permanent with complete excision. Very low recurrence rate. The excised specimen is sent for histological analysis. Appropriate for all uninflamed cysts and for previously inflamed cysts that have settled completely.",
  },
  {
    title: "Minimal Excision Technique",
    body:
      "A modified approach that uses a smaller incision than standard excision. A small opening is made, the cyst contents are expressed, and the cyst wall is extracted through the smaller opening. Produces a shorter scar than standard excision. The trade-off is a slightly higher risk of incomplete wall removal due to the limited access. Appropriate for smaller, superficial cysts in cosmetically sensitive locations where scar minimization is the priority.",
  },
  {
    title: "Incision and Drainage",
    body:
      "Used only when a cyst is acutely infected and requires urgent decompression. The cyst is opened, the infected material is drained, and antibiotics are prescribed. This procedure treats the infection and relieves pain. It does not remove the cyst wall and does not prevent recurrence. Definitive surgical excision is planned after the infection has fully resolved, typically 4 to 6 weeks later. Drainage alone as a definitive treatment produces near-universal recurrence.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Sebaceous Cyst Treatment Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">How Sebaceous Cyst Removal is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 20 to 30 minutes under local anesthesia. No hospital admission is required. Dr. Jain marks the planned incision over the cyst before anesthesia, orienting the incision along natural skin tension lines to minimize scar visibility.
            </p>
            <p>
              After anesthesia, the incision is made and the cyst is carefully dissected from the surrounding tissue. The goal is to keep the cyst wall intact during dissection and remove the entire sac as a single unit. The punctum is included in the excision to ensure the follicular opening where the blockage originated is removed alongside the cyst body.
            </p>
            <p>
              The wound is closed in two layers. The deep tissue is approximated first to eliminate the space left by the removed cyst. The skin is closed with fine sutures placed in the most cosmetically favorable orientation for that location. A simple dressing is applied. The excised specimen is sent for histological analysis. Most results confirm the benign cyst diagnosis.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild swelling and tenderness at the removal site. Normal and expected. A simple dressing covers the wound. Standard pain medication manages any discomfort.",
      icon: BedDouble,
    },
    {
      period: "Day 7",
      description:
        "Sutures are removed at the post-operative visit. The wound is assessed. Most patients are comfortable and fully mobile by this point.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description:
        "Normal healing is complete for most patients. All normal activities have resumed. Scar care with silicone gel or sheeting begins to optimize the long-term scar appearance.",
      icon: Clock,
    },
    {
      period: "Months 1 to 6",
      description:
        "The scar matures and fades. Cysts on cosmetically sensitive areas like the face typically heal with scars that are difficult to see within 6 months when scar care is followed. The result is permanent.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">What to Expect</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldConsiderSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6">Who Should Consider Sebaceous Cyst Removal?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any adult with a confirmed or suspected sebaceous cyst who wants it removed for any reason, whether cosmetic, symptomatic, or to prevent future complications, is an appropriate candidate for consultation. There is no minimum size requirement and no symptom threshold. A small cyst on a cosmetically important area that the patient wants removed is a valid indication. A large cyst that has been stable for years but is causing increasing concern is equally valid.
            </p>
            <p>
              Patients whose cyst is currently infected should seek assessment promptly for drainage and antibiotic treatment, then return for elective excision once the inflammation has fully resolved. Patients with multiple cysts can have several removed in a single session when their locations allow. Patients in good general health without active skin infection overlying the cyst are appropriate for local anesthesia excision in a clinic setting without any special preparation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {CYST_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`cyst-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Cyst Removal Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines the cyst, assesses whether it is currently inflamed or infected, and advises on the appropriate timing and technique for removal. Patients with an active infection are treated promptly and scheduled for definitive excision once the inflammation resolves. You leave with a clear plan and a realistic picture of the scar outcome for your specific cyst location.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Lipoma Removal",
      description: "Removal of benign fatty lumps that are sometimes confused with sebaceous cysts.",
      href: LIPOMA_PATH,
    },
    {
      name: "Fibroadenoma Treatment",
      description: "Assessment and removal of benign breast lumps requiring similar evaluation and surgical planning.",
      href: "/fibroadenoma-breast-lump-treatment-mumbai",
    },
    {
      name: "Scar Revision",
      description: "Improving the appearance of scars from previous cyst drainage, rupture, or incomplete removal.",
      href: "/hypertrophic-scar-treatment-in-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SebaceousCystRemovalPage() {
  return (
    <>
      <SEO
        title="Sebaceous Cyst Removal | Dr. Vicky Jain"
        description="Recurring, infected, or growing sebaceous cyst causing concern. Dr. Vicky Jain performs complete cyst removal with minimal scarring. Book your consultation."
        canonical="/sebaceous-cyst-removal-mumbai"
      />
      <CystJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhenRemovalSection />
      <CystVsLipomaSection />
      <IfIgnoredSection />
      <TreatmentOptionsSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <WhoShouldConsiderSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
